import type { OpenPaymentOptions, ArtoPaySDK, SDKConfiguration } from './types';
import { isOpen, setCallbacks } from './state';
import { createModal, closeModal } from './modal';
import { configure as configureSDK, getPublicKey, isSandboxMode } from './config';

export type {
  TransactionStatus,
  PaymentResultStatus,
  PaymentResult,
  PaymentCallbacks,
  OpenPaymentOptions,
  ArtoPaySDK,
  SDKConfiguration,
} from './types';

// A script-tag install configures the SDK from the tag's attributes. `document.currentScript` is only set
// while a classic script runs, so it is empty for ES module imports and absent when rendering on a server;
// those set the public key with configure() or per payment instead.
const currentScript = typeof document !== 'undefined' ? document.currentScript : null;
const scriptPublicKey = currentScript?.getAttribute('data-client-key') || undefined;
const scriptSandbox = currentScript?.getAttribute('data-sandbox');

configureSDK({
  sandbox: scriptSandbox !== 'false',
  ...(scriptPublicKey ? { publicKey: scriptPublicKey } : {}),
});

function openPayment(options: OpenPaymentOptions): void {
  if (isOpen()) {
    console.warn('[arto-pay/js-sdk] Payment modal is already open');
    return;
  }

  const { clientSecret, paymentId, token, onSuccess, onPending, onError, onClose, sandbox, orderId } = options;

  if (!clientSecret) {
    throw new Error('[arto-pay/js-sdk] Payment clientSecret is required');
  }

  if (!paymentId) {
    throw new Error('[arto-pay/js-sdk] Payment paymentId is required');
  }

  if (!orderId) {
    throw new Error('[arto-pay/js-sdk] Payment orderId is required');
  }

  const publicKey = options.publicKey || getPublicKey();
  if (!publicKey) {
    throw new Error(
      '[arto-pay/js-sdk] Payment publicKey is required: set data-client-key on the script tag, ' +
        'call configure({ publicKey }), or pass publicKey to openPayment'
    );
  }

  setCallbacks({
    onSuccess,
    onPending,
    onError,
    onClose,
  });

  // A per-payment sandbox choice applies to this payment only; it must not change the SDK-wide setting.
  createModal(clientSecret, paymentId, publicKey, token, orderId, sandbox ?? isSandboxMode());
}

function closePayment(): void {
  closeModal();
}

function isPaymentOpen(): boolean {
  return isOpen();
}

function configure(config: Partial<SDKConfiguration>): void {
  configureSDK(config);
}

function isSandbox(): boolean {
  return isSandboxMode();
}

const ArtoPay: ArtoPaySDK = {
  openPayment,
  closePayment,
  isPaymentOpen,
  configure,
  isSandbox,
};

export { openPayment, closePayment, isPaymentOpen, configure, isSandbox };
export default ArtoPay;
